import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import { FormInput } from 'app/components/Input/FormInput';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { useQueryRequest } from 'app/components/Table/core/QueryRequestProvider';
import { useQueryResponse } from 'app/components/Table/core/QueryResponseProvider';
import { emptyListViewProvider } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { getResponseError } from 'app/helpers/errors';
import { useBoolean, useNumber } from 'app/hooks';
import { isEqual } from 'lodash';

import { createOrg, updateOrg } from '../core/requests';
import { useFormik } from 'formik';
import { OrganizationInfo, useOrganizationStore } from 'theme/layout/components/aside/store';
import * as Yup from 'yup';

type Props = {
  organization: OrganizationInfo;
  titleNotification: string;
  originIsView?: boolean;
};

const FormOrganizationTableCard: FC<Props> = ({
  organization,
  titleNotification,
  originIsView = false,
}) => {
  const { refetch } = useQueryResponse();
  const { itemForUpdate, setItemForUpdate } = useListView();

  const { updateState, state } = useQueryRequest();
  const isView = useBoolean(originIsView);

  const [formRequest] = useState<{
    name: string;
  }>({
    name: organization ? organization.name : '',
  });
  const organizationId = useNumber(0);

  useEffect(() => {
    isView.setValue(originIsView);
  }, [originIsView]);

  useEffect(() => {
    if (!organization) {
      return;
    }
    organizationId.setValue(organization.id);
  }, [JSON.stringify(organization)]);

  const getSchema = () => {
    return {
      name: Yup.string().trim().required(`${titleNotification} name is required`),
    };
  };

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();

      useOrganizationStore.getState().forceRefreshList();
    }
    setItemForUpdate(emptyListViewProvider);
  };

  const formik = useFormik({
    initialValues: formRequest,
    validationSchema: Yup.object().shape(getSchema()),
    onSubmit: async (values, { setSubmitting }) => {
      if (isView.value) {
        isView.setValue(false);
        if (itemForUpdate) {
          setItemForUpdate({
            ...itemForUpdate,
            isView: false,
          });
        }
        return;
      }
      setSubmitting(true);

      try {
        const request: { name: string } = {
          name: values.name,
        };

        if (isEqual(organization?.name, request.name)) {
          cancel(true);
          return;
        }

        if (organization) {
          const dataRes = await updateOrg(organization.id, request.name);
          toast.success(`Update organization successfully!`);
          if (dataRes) {
            useOrganizationStore
              .getState()
              .setOrganization(dataRes.id, dataRes.name, dataRes.logo || '');
            cancel(true);
          }
          return;
        }
        await createOrg(request);
        toast.success(`Add organization successfully!`);
        refetch();
        useOrganizationStore.getState().forceRefreshList();
        if (state.page !== 1) {
          cancel();
          updateState({
            page: 1,
          });
          return;
        }
        cancel(true);
      } catch (error: any) {
        getResponseError(error);
      } finally {
        setSubmitting(true);
      }
    },
  });
  const renderLabelButtonSubmit = () => {
    if (organization) {
      return isView.value ? 'Edit' : 'Save';
    }
    return 'Add';
  };

  return (
    <>
      <form id="kt_modal_add_org_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div
          className="d-flex flex-column scroll-y me-n7 pe-7 ps-1"
          id="kt_modal_add_user_scroll"
          data-kt-scroll="true"
          data-kt-scroll-activate="{default: false, lg: true}"
          data-kt-scroll-max-height="auto"
          data-kt-scroll-dependencies="#kt_modal_add_user_header"
          data-kt-scroll-wrappers="#kt_modal_add_org_form"
          data-kt-scroll-offset="300px"
        >
          <FormInput
            label={`${titleNotification} name`}
            placeholder={`${titleNotification} name`}
            formik={formik}
            field="name"
            isRequired
            isDisable={isView.value}
          />
        </div>
        <ActionButton
          onCancel={() => cancel()}
          disableButton={formik.isSubmitting || !formik.isValid || !formik.touched}
          isSubmitting={formik.isSubmitting}
          labelButtonSubmit={renderLabelButtonSubmit()}
        />
      </form>
      {formik.isSubmitting && <LoadingCard />}
    </>
  );
};

export { FormOrganizationTableCard };
